import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import {
  WeekInfo,
  formatWeek,
  isSameWeek,
  getCurrentWeek,
  getPreviousWeek,
  getNextWeek,
} from '@/utils/week';
import { useState, useMemo, useRef, useEffect } from 'react';

interface WeekNavigatorProps {
  selectedWeek: WeekInfo;
  onWeekChange: (week: WeekInfo) => void;
}

/**
 * Generate a list of weeks for the dropdown
 * - 8 weeks in the past (initially)
 * - Current week
 * - 4 weeks in the future
 */
function generateWeekList(
  currentWeek: WeekInfo,
  pastWeeksCount: number = 8
): WeekInfo[] {
  const weeks: WeekInfo[] = [];

  // Start from pastWeeksCount weeks in the past
  let week = currentWeek;
  for (let i = 0; i < pastWeeksCount; i++) {
    week = getPreviousWeek(week);
  }

  // Generate weeks (past + current + 4 future)
  const totalWeeks = pastWeeksCount + 1 + 4;
  for (let i = 0; i < totalWeeks; i++) {
    weeks.push(week);
    week = getNextWeek(week);
  }

  // Return in descending order (most recent first)
  return weeks.reverse();
}

export function WeekNavigator({
  selectedWeek,
  onWeekChange,
}: WeekNavigatorProps) {
  const colors = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [hoveredWeek, setHoveredWeek] = useState<string | null>(null);
  const [pastWeeksCount, setPastWeeksCount] = useState(8);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentWeek = getCurrentWeek();

  // Generate week list (memoized to avoid recalculation)
  const weekList = useMemo(
    () => generateWeekList(currentWeek, pastWeeksCount),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally using primitives instead of full currentWeek object which is recreated each render
    [currentWeek.week, currentWeek.year, pastWeeksCount]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle scroll to load more past weeks
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrolledToBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (scrolledToBottom && pastWeeksCount < 52) {
      // Load 8 more weeks when user scrolls near bottom
      setPastWeeksCount(prev => Math.min(prev + 8, 52));
    }
  };

  const handleWeekSelect = (week: WeekInfo) => {
    onWeekChange(week);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Week selector button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 h-11 rounded-lg transition-all duration-200 font-medium text-base min-w-0"
        style={{
          backgroundColor: isButtonPressed
            ? colors.active.background
            : isButtonHovered || isOpen
              ? colors.hover.background
              : 'transparent',
          color: isButtonPressed
            ? colors.active.text
            : isButtonHovered || isOpen
              ? colors.hover.text
              : colors.text.primary,
        }}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => {
          setIsButtonHovered(false);
          setIsButtonPressed(false);
        }}
        onMouseDown={() => setIsButtonPressed(true)}
        onMouseUp={() => setIsButtonPressed(false)}
        title={formatWeek(selectedWeek, 'long')}
      >
        <Icon name={IconName.CALENDAR} size="sm" className="flex-shrink-0" />
        <span className="hidden lg:inline truncate min-w-0">
          {formatWeek(selectedWeek, 'short')}
        </span>
        <Icon
          name={IconName.CHEVRON_DOWN}
          size="sm"
          className="flex-shrink-0"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {/* Custom dropdown */}
      {isOpen && (
        <div
          className="absolute top-full mt-2 right-0 rounded-lg shadow-lg overflow-hidden z-50"
          style={{
            backgroundColor: colors.offWhite,
            border: `1px solid ${colors.border.default}`,
            minWidth: 'calc(280px * var(--sizing-multiplier, 1))',
            maxHeight: 'calc(400px * var(--sizing-multiplier, 1))',
          }}
        >
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-y-auto"
            style={{ maxHeight: 'calc(400px * var(--sizing-multiplier, 1))' }}
          >
            {weekList.map(week => {
              const isCurrentWeekItem = isSameWeek(week, currentWeek);
              const isSelected = isSameWeek(week, selectedWeek);
              const weekKey = `${week.year}-W${week.week}`;
              const isHovered = hoveredWeek === weekKey;

              return (
                <button
                  key={weekKey}
                  onClick={() => handleWeekSelect(week)}
                  className="w-full text-left px-4 h-11 transition-colors duration-150 flex items-center"
                  style={{
                    backgroundColor: isSelected
                      ? colors.primary
                      : isHovered
                        ? colors.hover.background
                        : 'transparent',
                    color: isSelected
                      ? colors.offWhite
                      : isHovered
                        ? colors.hover.text
                        : colors.text.primary,
                  }}
                  onMouseEnter={() => setHoveredWeek(weekKey)}
                  onMouseLeave={() => setHoveredWeek(null)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium">
                      {formatWeek(week, 'long')}
                    </span>
                    {isCurrentWeekItem && (
                      <span
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: colors.success,
                          color: colors.offWhite,
                        }}
                      >
                        This Week
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {pastWeeksCount < 52 && (
              <div
                className="text-center py-2 text-sm"
                style={{ color: colors.text.secondary }}
              >
                Scroll for more weeks...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
